import React, { PropsWithChildren, ReactNode, useId } from 'react';

import { Fade, MenuList } from '@mui/material';
import { default as MUIMenu, MenuProps as MUIMenuProps } from '@mui/material/Menu';
import MenuItem, { MenuItemProps } from '@mui/material/MenuItem';
import classNames from 'classnames';

import { IconCheck2 } from '@/blocks/components/Icons';
import { WithClassName } from '@/blocks/components/types';
import scrollbar from '@/styles/scrollbar.module.scss';

import styles from './styles.module.scss';

export type MenuProps = {
  anchorEl: HTMLElement | null;
  renderButton: (buttonId: string, menuId: string) => ReactNode;
  open: boolean;
  handleClose(): void;
  children: ReactNode;
} & MUIMenuProps &
  WithClassName;

export const Menu = ({
  children,
  renderButton,
  anchorEl,
  open,
  handleClose,
  className,
  ...rest
}: MenuProps) => {
  const buttonId = useId();
  const menuId = useId();

  return (
    <>
      {renderButton(buttonId, menuId)}
      <MUIMenu
        id={menuId}
        className={classNames(styles.selectMenu, className)}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
        MenuListProps={{
          'aria-labelledby': buttonId,
        }}
        disableScrollLock
        transitionDuration={60}
        autoFocus={false}
        {...rest}
      >
        {children}
      </MUIMenu>
    </>
  );
};

export type SelectItemProps = {
  children: ReactNode;
  icon?: ReactNode | boolean;
} & MenuItemProps;

export const SelectItem = ({ children, icon, ...rest }: SelectItemProps) => {
  return (
    <MenuItem className={classNames(styles.selectItem)} disableRipple {...rest}>
      <div className={styles.selectItemActiveIcon}>{rest.selected ? <IconCheck2 /> : null}</div>
      {icon ? <div className={styles.selectItemIcon}>{icon}</div> : null}
      {children}
    </MenuItem>
  );
};

export const SelectItemsList = ({ children }: PropsWithChildren) => {
  return (
    <MenuList className={classNames(styles.selectItems, scrollbar.customScrollbar)}>{children}</MenuList>
  );
};

export { useMenu } from './useMenu';
